'use client'
import React, { ReactElement, ReactNode, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useWizardSelector } from '@/components/ui/wizard/WizardState'
import classNames from 'classnames'
import LoadingIndicator from '@/components/elements/LoadingIndicator'
import Button from '@/components/common/button/Button'
import { onMobileOrTablet } from '@/lib/utils/utils'
import { WizardStep } from '@/components/ui/wizard/Wizard'
import { setActiveStep } from '@/components/ui/wizard/WizardActions'
import { ButtonType } from '@/components/common/button/ButtonType'
import { useTranslations } from 'next-intl'

export interface DropDownFooterProps {
  resetAction?: () => void
  searchWizard: boolean | undefined
  step: number
  nextButtonActiveLandingPage: boolean
  specialNextAction?: (e: React.MouseEvent<HTMLButtonElement>) => void
  options?: ReactNode
  primaryActionLabel?: string
  hideSkipButton?: boolean
  absolutePositioned?: boolean
}

export default function DropDownFooter(props: DropDownFooterProps): ReactElement {
  const dispatch = useDispatch()
  const activeStep = useWizardSelector<number | undefined>((state) => state.activeStep)
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false)
  const t = useTranslations('Wizard')

  function nextStep(): void {
    dispatch(setActiveStep(props.step + 1))
  }

  function close(): void {
    dispatch(setActiveStep(-1))
  }

  function nextButtonClickHandler(e: React.MouseEvent<HTMLButtonElement>): void {
    setShowLoadingIndicator(true)

    if (props.specialNextAction) {
      props.specialNextAction(e)
    } else {
      if (props.searchWizard) {
        close()
      } else {
        nextStep()
      }
    }
  }

  function primaryActionLabel(): string {
    if (props.primaryActionLabel) {
      return props.primaryActionLabel
    }

    return props.searchWizard ? 'closeButton' : 'nextButtonLabel'
  }

  function nextButtonActive(): boolean {
    return props.searchWizard || props.nextButtonActiveLandingPage
  }

  function hideSkipOrCloseButton(): boolean {
    return !!(props.hideSkipButton || (props.searchWizard && !onMobileOrTablet()))
  }

  function skipOrClose(): void {
    props.searchWizard && onMobileOrTablet() ? close() : nextStep()
  }

  return (
    <div
      className={classNames(
        'wizard-container left-0 right-0 z-[10000] flex items-center justify-between border-t-[1px] border-t-[rgba(0,0,0,0.1)] text-stone-700',
        {
          'search z-20 wd:flex wd:rounded-b-lg wd:bg-white wd:py-3 wm:fixed wm:h-[auto] wm:flex-col wm:bg-white wm:pb-4 wm:pt-4':
            props.searchWizard,
          'landing-page wdl:flex wdl:rounded-b-lg wdl:bg-white wdl:py-3 wml:fixed wml:h-[auto] wml:flex-col wml:bg-white wml:pb-4 wml:pt-4':
            !props.searchWizard,
        },
      )}
    >
      {!props.searchWizard && (
        <div
          className={classNames(
            'wml:row-1 absolute left-0 top-0 h-[5px] bg-primary duration-200 wdl:invisible wml:visible',
            {
              'w-[calc(100vw_*0.33)]': activeStep === WizardStep.DESTINATION,
              'w-[calc(100vw_*0.66)]': activeStep === WizardStep.TOPIC,
              'w-[100vw]': activeStep === WizardStep.TRAVEL_PERIOD,
            },
          )}
        />
      )}
      {props.options && (
        <div
          className={classNames('no-scrollbar mx-3 mb-[10px] w-full flex-grow ', {
            'wm:row-2 wd:mt-[7px] wm:overflow-x-scroll wm:border-b-[1px]': props.searchWizard,
            'wml:row-2 wdl:mt-[7px] wml:overflow-x-scroll wml:border-b-[1px]': !props.searchWizard,
          })}
        >
          <div
            className={classNames('flex w-full min-w-min gap-x-2 gap-y-3.5 text-center', {
              'wd:ml-10 wm:mx-2 wm:mb-3 wm:mt-1 wm:w-[100%] wm:justify-center': props.searchWizard,
              'wdl:ml-10 wml:mx-2 wml:mb-3 wml:mt-1 wml:w-[100%] wml:justify-center':
                !props.searchWizard,
            })}
          >
            {props.options}
          </div>
        </div>
      )}
      <div
        className={classNames('flex', {
          'justify-between': !hideSkipOrCloseButton(),
          'justify-end': hideSkipOrCloseButton(),
          'w-full': !props.options,
          'wm:w-full': props.searchWizard,
          'wml:w-full': !props.searchWizard,
        })}
      >
        {!hideSkipOrCloseButton() && (
          <button
            className={classNames(
              'row-3 bottom-[12px] left-[12px] col-start-1 flex h-[44px] items-center pr-[10px] text-xl font-medium leading-[0px] text-primary wml:ml-2 wml:pl-[10px]',
              {
                'ml-3 pl-[10px]': !props.options || !props.searchWizard,
                'wd:mr-4': !props.searchWizard,
                'wdl:mr-4': props.searchWizard,
              },
            )}
            onClick={(e) => {
              props.specialNextAction ? props.specialNextAction(e) : skipOrClose()
            }}
          >
            {t(
              !props.searchWizard || (props.searchWizard && !onMobileOrTablet())
                ? 'skipButtonLabel'
                : 'closeButton',
            )}
          </button>
        )}
        {props.resetAction && (
          <div className="w-30 -wml:mb-1.5">
            <Button
              className={classNames('wdl:h-[36px] wml:h-[44px]', {
                'ml-5': !props.options,
              })}
              buttonType={ButtonType.SECONDARY}
              onClick={props.resetAction}
            >
              {t('filterOptionReset')}
            </Button>
          </div>
        )}
        <button
          className={classNames('z-[5100] mr-5 w-max overflow-hidden rounded-md font-semibold', {
            'wm:row-3 h-[44px] wd:px-7 wd:py-1 wd:text-md wm:col-start-2 wm:translate-y-0.5 wm:px-12 wm:py-2 wm:text-xl':
              props.searchWizard,
            'wml:row-3 h-[44px] wdl:px-7 wdl:py-1 wdl:text-md wml:col-start-2 wml:translate-y-0.5 wml:px-12 wml:py-2 wml:text-xl':
              !props.searchWizard,
            'bg-primary text-white': nextButtonActive(),
            'cursor-default bg-[#EDEDED] text-[#ADADAD]': !nextButtonActive(),
            'bg-primary ': showLoadingIndicator,
          })}
          onClick={(e) => {
            nextButtonActive() ? nextButtonClickHandler(e) : undefined
          }}
        >
          {t(primaryActionLabel())}
          {showLoadingIndicator && (
            <LoadingIndicator additionalClassName="absolute wml:left-[25%] wdl:left-[12%] wml:-top-[40%] wdl:-top-[17px] bg-primary scale-75" />
          )}
        </button>
      </div>
    </div>
  )
}
